/**
 * TwoFactorSettings — user-facing 2FA enrollment / management, with a choice of
 * method: authenticator app (TOTP) or a code sent to the secondary email.
 *
 * Talks to the self-service MFA endpoints on services/people:
 *   GET  /api/v1.0/mfa/                          → status (+ device_type, secondary_email_masked)
 *   POST /api/v1.0/mfa/totp/enroll/  {password?} → { secret, otpauth_uri, qr_svg }
 *   POST /api/v1.0/mfa/totp/confirm/  {code}     → { recovery_codes }
 *   POST /api/v1.0/mfa/email/enroll/ {email?, password?} → { sent_to, resend_after }
 *   POST /api/v1.0/mfa/email/confirm/ {code}     → { recovery_codes }
 *   POST /api/v1.0/mfa/disable/       {password} → 200
 *   POST /api/v1.0/mfa/recovery-codes/regenerate/ {password} → { recovery_codes }
 *
 * Only one method is active at a time; confirming one replaces the other.
 * While 2FA is on, switching method needs the account password (server step-up).
 */
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PasswordInput } from "@weco/ui";
import { peopleApiUrl } from "../config/peopleApi";
import { AUTHENTICATOR_APP_LINKS } from "../lib/authenticatorApps";
import { useShellStore } from "../store";

type Step = "loading" | "disabled" | "choose" | "enrolling" | "email" | "emailCode" | "codes" | "enabled";
type Method = "totp" | "email";

interface Enforcement {
  required: boolean;
  grace_period_days: number;
  deadline: string | null;
  days_remaining: number | null;
  blocked: boolean;
}
interface Status {
  enabled: boolean;
  device_type?: Method | null;
  secondary_email_masked?: string | null;
  recovery_codes_remaining: number;
  enforcement?: Enforcement;
}
interface Enrollment {
  secret: string;
  qr_svg: string;
}

class MfaRequestError extends Error {
  constructor(
    message: string,
    public status: number,
    public data: Record<string, unknown> | null,
  ) {
    super(message);
  }
}

/** Counts down to zero once a second; set(n) restarts it. */
function useCountdown(): [number, (n: number) => void] {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (left <= 0) return;
    const id = window.setTimeout(() => setLeft((v) => Math.max(0, v - 1)), 1000);
    return () => window.clearTimeout(id);
  }, [left]);
  return [left, setLeft];
}

export const TwoFactorSettings: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { getValidAccessToken } = useShellStore();
  const [step, setStep] = useState<Step>("loading");
  const [status, setStatus] = useState<Status | null>(null);
  const [enrollment, setEnrollment] = useState<Enrollment | null>(null);
  const [code, setCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [password, setPassword] = useState("");
  const [pendingAction, setPendingAction] = useState<"disable" | "regenerate" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Email method
  const [newEmail, setNewEmail] = useState("");
  const [useOtherEmail, setUseOtherEmail] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [wait, setWait] = useCountdown();

  const switching = !!status?.enabled;

  const authFetch = useCallback(
    async (path: string, body?: unknown) => {
      const access = await getValidAccessToken();
      if (!access) throw new Error(t("settings.mfa.sessionExpired"));
      const res = await fetch(peopleApiUrl(path), {
        method: body === undefined ? "GET" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${access}`,
        },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new MfaRequestError(data?.detail ?? t("settings.mfa.requestFailed"), res.status, data);
      return data;
    },
    [getValidAccessToken, t],
  );

  const resetForms = () => {
    setCode("");
    setPassword("");
    setNewEmail("");
    setUseOtherEmail(false);
    setSentTo(null);
    setError(null);
  };

  const loadStatus = useCallback(async () => {
    try {
      const data: Status = await authFetch("mfa");
      setStatus(data);
      setStep(data.enabled ? "enabled" : "disabled");
    } catch (e: any) {
      setError(e?.message ?? t("settings.mfa.loadFailed"));
      setStep("disabled");
    }
  }, [authFetch, t]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const backToStart = () => {
    resetForms();
    void loadStatus();
  };

  const beginEnroll = async () => {
    setError(null);
    setBusy(true);
    try {
      const data: Enrollment = await authFetch("mfa/totp/enroll", switching ? { password } : {});
      setEnrollment(data);
      setCode("");
      setStep("enrolling");
    } catch (e: any) {
      setError(e?.message ?? t("settings.mfa.startFailed"));
    } finally {
      setBusy(false);
    }
  };

  const confirmEnroll = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const path = step === "emailCode" ? "mfa/email/confirm" : "mfa/totp/confirm";
      const data = await authFetch(path, { code: code.trim() });
      setRecoveryCodes(data.recovery_codes ?? []);
      setPassword("");
      setStep("codes");
    } catch (e: any) {
      setCode("");
      setError(e?.message ?? t("settings.mfa.badCode"));
    } finally {
      setBusy(false);
    }
  };

  const sendEmailCode = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const body: Record<string, string> = {};
      const otherAddress = useOtherEmail || !status?.secondary_email_masked;
      if (otherAddress && newEmail.trim()) body.email = newEmail.trim();
      // The server wants the password when 2FA is on, or when an address on
      // file is being replaced; sending it when not needed is harmless.
      if (password) body.password = password;
      const data = await authFetch("mfa/email/enroll", body);
      setSentTo(data.sent_to ?? null);
      setWait(Number(data.resend_after) || 60);
      setCode("");
      setStep("emailCode");
    } catch (err: any) {
      if (err instanceof MfaRequestError) {
        if (err.status === 429) {
          setWait(Number(err.data?.retry_after) || 60);
          setError(t("settings.mfa.emailThrottled"));
        } else if (err.data?.code === "secondary_email_is_login") {
          setError(t("settings.mfa.emailIsLogin"));
        } else if (err.status === 503) {
          setError(t("settings.mfa.emailSendFailed"));
        } else {
          setError(err.message);
        }
      } else {
        setError(err?.message ?? t("settings.mfa.startFailed"));
      }
    } finally {
      setBusy(false);
    }
  };

  const runPasswordAction = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (pendingAction === "disable") {
        await authFetch("mfa/disable", { password });
        setPassword("");
        setPendingAction(null);
        await loadStatus();
      } else if (pendingAction === "regenerate") {
        const data = await authFetch("mfa/recovery-codes/regenerate", { password });
        setPassword("");
        setPendingAction(null);
        setRecoveryCodes(data.recovery_codes ?? []);
        setStep("codes");
      }
    } catch (e: any) {
      setError(e?.message ?? t("settings.mfa.actionFailed"));
    } finally {
      setBusy(false);
    }
  };

  const copyCodes = () => {
    navigator.clipboard?.writeText(recoveryCodes.join("\n")).catch(() => {});
  };

  // Styling follows the design's Settings cards (weco2/settings.jsx):
  // semantic tokens only, brand colour via bg-primary / text-primary-foreground.
  const btnPrimary =
    "px-4 h-10 rounded-2xl text-sm font-bold bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed";
  const btnOutline =
    "px-4 h-10 rounded-2xl text-sm font-bold border border-border text-foreground hover:bg-muted transition-colors disabled:opacity-60 disabled:cursor-not-allowed";
  const btnGhost =
    "px-3 h-10 rounded-2xl text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-muted transition-colors";
  const btnDanger =
    "px-4 h-10 rounded-2xl text-sm font-bold bg-red-500 text-white hover:bg-red-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed";
  const hint = "text-sm text-muted-foreground mb-4 text-start";
  const textInput =
    "h-10 px-3 rounded-xl text-sm border border-border bg-muted/40 outline-none focus:bg-background focus:border-primary transition-colors";
  const linkBtn = "text-sm font-semibold text-primary hover:underline disabled:opacity-50 disabled:no-underline";

  const errorBox = error ? (
    <p role="alert" className="text-sm px-4 py-3 rounded-2xl mb-4 bg-red-500/10 text-red-600 dark:text-red-400 text-start">
      {error}
    </p>
  ) : null;

  const storeLinks = (
    <p className="text-xs text-muted-foreground text-start flex flex-wrap items-center gap-x-2 gap-y-1" data-testid="mfa-store-links">
      <span>{t("settings.mfa.getApp")}</span>
      <a href={AUTHENTICATOR_APP_LINKS.ios} target="_blank" rel="noopener noreferrer" className="font-semibold text-primary hover:underline">
        {t("settings.mfa.appStore")}
      </a>
      <span aria-hidden="true">·</span>
      <a href={AUTHENTICATOR_APP_LINKS.android} target="_blank" rel="noopener noreferrer" className="font-semibold text-primary hover:underline">
        {t("settings.mfa.googlePlay")}
      </a>
    </p>
  );

  const codeInput = (
    <input
      type="text"
      inputMode="numeric"
      autoComplete="one-time-code"
      value={code}
      onChange={(e) => setCode(e.target.value)}
      placeholder="123456"
      required
      dir="ltr"
      aria-label={t("settings.mfa.codeLabel")}
      className={`${textInput} tracking-widest w-36`}
    />
  );

  if (step === "loading") {
    return <p className="text-sm text-muted-foreground">{t("settings.mfa.loading")}</p>;
  }

  if (step === "codes") {
    return (
      <div>
        <p className={hint}>{t("settings.mfa.codesHint")}</p>
        <div
          dir="ltr"
          className="grid grid-cols-2 gap-2 font-mono text-sm p-4 rounded-2xl mb-4 bg-muted/30 border border-border text-foreground"
        >
          {recoveryCodes.map((c) => (
            <span key={c}>{c}</span>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={copyCodes} className={btnOutline}>
            {t("settings.mfa.copy")}
          </button>
          <button type="button" onClick={backToStart} className={btnPrimary}>
            {t("settings.mfa.savedThem")}
          </button>
        </div>
      </div>
    );
  }

  if (step === "enrolling" && enrollment) {
    return (
      <div>
        {errorBox}
        <p className={hint}>{t("settings.mfa.scanHint")}</p>
        <div className="mb-4">{storeLinks}</div>
        <div
          className="inline-block bg-white p-3 rounded-2xl border border-border mb-4 [&_svg]:w-40 [&_svg]:h-40"
          dangerouslySetInnerHTML={{ __html: enrollment.qr_svg }}
        />
        <p className="text-xs text-muted-foreground mb-4 text-start">
          {t("settings.mfa.manualKey")} <span className="font-mono text-foreground break-all" dir="ltr">{enrollment.secret}</span>
        </p>
        <form onSubmit={confirmEnroll} className="flex flex-wrap items-center gap-2">
          {codeInput}
          <button type="submit" disabled={busy} className={btnPrimary}>
            {busy ? t("settings.mfa.verifying") : t("settings.mfa.confirm")}
          </button>
          <button type="button" onClick={backToStart} className={btnGhost}>
            {t("settings.mfa.cancel")}
          </button>
        </form>
      </div>
    );
  }

  if (step === "email") {
    const onFile = status?.secondary_email_masked ?? null;
    const askAddress = !onFile || useOtherEmail;
    // Replacing an address on file, or changing method while 2FA is on, needs the password.
    const needPassword = switching || (!!onFile && useOtherEmail);
    return (
      <form onSubmit={sendEmailCode}>
        {errorBox}
        {askAddress ? (
          <div className="mb-4 text-start">
            <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="mfa-secondary-email">
              {t("settings.mfa.emailLabel")}
            </label>
            <input
              id="mfa-secondary-email"
              type="email"
              dir="ltr"
              autoComplete="email"
              required
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              className={`${textInput} w-72 max-w-full`}
            />
            <p className="text-xs text-muted-foreground mt-1.5">{t("settings.mfa.emailHint")}</p>
          </div>
        ) : (
          <p className={hint}>
            {t("settings.mfa.emailWillSend")} <strong className="text-foreground" dir="ltr">{onFile}</strong>
            {" · "}
            <button type="button" className={linkBtn} onClick={() => setUseOtherEmail(true)}>
              {t("settings.mfa.useOtherEmail")}
            </button>
          </p>
        )}
        {needPassword && (
          <div className="mb-4 w-56 max-w-full">
            <PasswordInput
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("settings.mfa.password")}
              required
              autoComplete="current-password"
            />
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <button type="submit" disabled={busy || wait > 0} className={btnPrimary}>
            {busy ? t("settings.mfa.sending") : t("settings.mfa.sendCode")}
          </button>
          <button type="button" onClick={backToStart} className={btnGhost}>
            {t("settings.mfa.cancel")}
          </button>
        </div>
      </form>
    );
  }

  if (step === "emailCode") {
    return (
      <div>
        {errorBox}
        <p className={hint}>
          {t("settings.mfa.emailSent")} <strong className="text-foreground" dir="ltr">{sentTo}</strong>
          {" "}
          {t("settings.mfa.emailExpiry")}
        </p>
        <form onSubmit={confirmEnroll} className="flex flex-wrap items-center gap-2 mb-3">
          {codeInput}
          <button type="submit" disabled={busy} className={btnPrimary}>
            {busy ? t("settings.mfa.verifying") : t("settings.mfa.confirm")}
          </button>
          <button type="button" onClick={backToStart} className={btnGhost}>
            {t("settings.mfa.cancel")}
          </button>
        </form>
        <button type="button" className={linkBtn} disabled={busy || wait > 0} onClick={() => void sendEmailCode()} data-testid="mfa-email-resend">
          {wait > 0 ? t("settings.mfa.resendIn", { count: wait }) : t("settings.mfa.resend")}
        </button>
      </div>
    );
  }

  if (pendingAction) {
    return (
      <form onSubmit={runPasswordAction}>
        {errorBox}
        <p className={hint}>
          {pendingAction === "disable"
            ? t("settings.mfa.disablePassword")
            : t("settings.mfa.regeneratePassword")}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <div className="w-56 max-w-full">
            <PasswordInput
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("settings.mfa.password")}
              required
              autoComplete="current-password"
            />
          </div>
          <button type="submit" disabled={busy} className={pendingAction === "disable" ? btnDanger : btnPrimary}>
            {busy ? t("settings.mfa.working") : pendingAction === "disable" ? t("settings.mfa.turnOff") : t("settings.mfa.regenerate")}
          </button>
          <button
            type="button"
            onClick={() => { setPendingAction(null); setPassword(""); setError(null); }}
            className={btnGhost}
          >
            {t("settings.mfa.cancel")}
          </button>
        </div>
      </form>
    );
  }

  // Method choice — the first enable, or "Switch method" while 2FA is on.
  const methodChoice = (
    <>
      {switching && (
        <div className="mb-4 text-start">
          <p className={hint}>{t("settings.mfa.switchPassword")}</p>
          <div className="w-56 max-w-full">
            <PasswordInput
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("settings.mfa.password")}
              autoComplete="current-password"
            />
          </div>
        </div>
      )}
      <div className="grid gap-2 sm:grid-cols-2 mb-3" role="group" aria-label={t("settings.mfa.chooseMethod")}>
        <button
          type="button"
          onClick={beginEnroll}
          disabled={busy || (switching && !password)}
          className="text-start p-4 rounded-2xl border border-border hover:bg-muted/40 hover:border-primary transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          data-testid="mfa-method-app"
        >
          <span className="block text-sm font-bold text-foreground">{t("settings.mfa.methodApp")}</span>
          <span className="block text-xs text-muted-foreground mt-1">{t("settings.mfa.methodAppDesc")}</span>
        </button>
        <button
          type="button"
          onClick={() => {
            setError(null);
            setStep("email");
          }}
          disabled={busy || (switching && !password)}
          className="text-start p-4 rounded-2xl border border-border hover:bg-muted/40 hover:border-primary transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          data-testid="mfa-method-email"
        >
          <span className="block text-sm font-bold text-foreground">{t("settings.mfa.methodEmail")}</span>
          <span className="block text-xs text-muted-foreground mt-1">
            {status?.secondary_email_masked ? (
              <>
                {t("settings.mfa.methodEmailDescTo")} <span dir="ltr">{status.secondary_email_masked}</span>
              </>
            ) : (
              t("settings.mfa.methodEmailDesc")
            )}
          </span>
        </button>
      </div>
      {storeLinks}
    </>
  );

  if (step === "choose") {
    return (
      <div>
        {errorBox}
        {methodChoice}
        <div className="mt-4">
          <button type="button" onClick={backToStart} className={btnGhost}>
            {t("settings.mfa.cancel")}
          </button>
        </div>
      </div>
    );
  }

  if (step === "enabled") {
    const isEmail = status?.device_type === "email";
    return (
      <div>
        {errorBox}
        <div className="flex items-start gap-3 p-4 rounded-2xl bg-muted/30 border border-border mb-4 text-start">
          <span className="mt-0.5 shrink-0 inline-flex items-center h-6 px-2.5 rounded-full text-[10px] font-black uppercase tracking-widest bg-green-500/15 text-green-700 dark:text-green-400">
            {t("settings.mfa.on").replace(/[.۔]\s*$/, "")}
          </span>
          <div className="text-sm text-muted-foreground">
            <p className="font-semibold text-foreground" data-testid="mfa-active-method">
              {isEmail ? t("settings.mfa.methodEmail") : t("settings.mfa.methodApp")}
              {isEmail && status?.secondary_email_masked ? (
                <>
                  {" · "}
                  <span dir="ltr">{status.secondary_email_masked}</span>
                </>
              ) : null}
            </p>
            <p>
              {isEmail ? t("settings.mfa.onHintEmail") : t("settings.mfa.onHint")}
              {status ? ` ${t("settings.mfa.codesRemaining", { count: status.recovery_codes_remaining })}` : ""}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => {
              resetForms();
              setStep("choose");
            }}
            className={btnOutline}
          >
            {t("settings.mfa.switchMethod")}
          </button>
          <button type="button" onClick={() => setPendingAction("regenerate")} className={btnOutline}>
            {t("settings.mfa.regenerateCodes")}
          </button>
          <button
            type="button"
            onClick={() => setPendingAction("disable")}
            className="px-4 h-10 rounded-2xl text-sm font-bold border border-border text-red-600 dark:text-red-400 hover:bg-red-500/10 transition-colors"
          >
            {t("settings.mfa.turnOff")}
          </button>
        </div>
      </div>
    );
  }

  // disabled
  const enf = status?.enforcement;
  const dateLocale = i18n.language?.startsWith("ar") ? "ar" : undefined;
  const deadlineText = enf?.deadline
    ? new Date(enf.deadline).toLocaleDateString(dateLocale, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : null;
  const enforcementCopy = enf?.blocked
    ? t("settings.mfa.orgRequiredNow")
    : deadlineText
      ? t("settings.mfa.orgRequiredBy", {
          date: deadlineText,
          count: enf?.days_remaining ?? 0,
        })
      : t("settings.mfa.orgRequired");
  return (
    <div>
      {errorBox}
      {enf?.required && (
        <div
          role="status"
          className={`text-sm px-4 py-3 rounded-2xl mb-4 text-start ${
            enf.blocked
              ? "bg-red-500/10 text-red-600 dark:text-red-400"
              : "bg-amber-500/10 text-amber-800 dark:text-amber-300"
          }`}
        >
          {enforcementCopy}
        </div>
      )}
      <p className={hint}>{t("settings.mfa.addStep")}</p>
      {methodChoice}
    </div>
  );
};

export default TwoFactorSettings;
